/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useEffect, useState } from 'react';
import { UniversalTrackersTable } from '../../../components/universal-trackers-table';
import { useAppDispatch, useAppSelector } from '../../../reducers';
import { SdgContributionGraph } from '../../../components/impact-performance/SdgContributionGraph';
import { canAccessCurrentLevel, isUserManagerByInitiativeId } from '../../../selectors/user';
import { loadScorecardsByInitiativeId, loadScorecardsBySurveyId } from '../../../actions/scorecard';
import { reloadInitiative } from '../../../actions/initiative';
import { MaterialityModal } from '../../../components/target-actual/MaterialityModal';
import IconButton from '../../../components/button/IconButton';
import { loadInsightsByPeriod, loadInsightsBySurveyId } from '../../../slice/insightsSlice';
import { useParams } from 'react-router-dom';
import { DashboardSectionTitle } from '../../../components/dashboard';
import { InsightsProps } from '../insightTypes';
import { FeaturePermissions } from '../../../services/permissions/FeaturePermissions';

export const InsightsOverview = (props: InsightsProps) => {
  const { readOnly, initiativeId, selectedPack, utTableUsage, surveyId, reportSwitcher, sdgCode, handleSDGChange, utrTable } = props;

  const canManage = useAppSelector((state) => isUserManagerByInitiativeId(state, initiativeId));
  const canAccessAITools = useAppSelector(FeaturePermissions.canAccessAITools);
  const { portfolioId } = useParams<{ portfolioId?: string }>();
  const [materialityModal, setMaterialityModal] = useState(false)

  const initiative = useAppSelector((state) => state.initiative.data);
  const scorecardState = useAppSelector((state) => surveyId ? state.scorecardSurvey : state.scorecard)
  const canAccess = useAppSelector((state) => canAccessCurrentLevel(state));

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (surveyId) {
      dispatch(loadScorecardsBySurveyId(initiativeId, surveyId));
      dispatch(loadInsightsBySurveyId(initiativeId, 'insights', surveyId, portfolioId));
    } else {
      // Reload insights, to clear stale data when we don't have surveyId
      dispatch(loadInsightsByPeriod(initiativeId, selectedPack, undefined, portfolioId));
      dispatch(loadScorecardsByInitiativeId(initiativeId));
    }
  }, [dispatch, initiativeId, surveyId, portfolioId, selectedPack])

  const toggleMaterialityModal = () => {
    setMaterialityModal(!materialityModal)
  }

  const materialityToggle =  <IconButton className='fas sdg-materiality-editbtn' onClick={() => toggleMaterialityModal()} icon='fa-pen' />

  const handleReload = () => {
    dispatch(reloadInitiative())
    if (surveyId) {
      dispatch(loadScorecardsBySurveyId(initiativeId, surveyId, false, undefined, true))
    } else {
      dispatch(loadScorecardsByInitiativeId(initiativeId, false, undefined, true))
    }
  }

  return <>
    <SdgContributionGraph
      canAccess={canAccess}
      showAITools={canAccessAITools}
      scorecardState={scorecardState}
      toggleMaterialityBtn={canManage ? materialityToggle : <></>}
      readOnly={readOnly}
      surveyId={surveyId}
      reportSwitcher={reportSwitcher}
      initiativeId={initiativeId}
      handleChangeSDG={handleSDGChange}
      sdgCode={sdgCode} />
    <DashboardSectionTitle title='Targets and Trends' headingStyle={2} className='text-ThemeHeadingLight'/>
    {utrTable ?
      <UniversalTrackersTable readOnly={!canManage} header={'Targets and trends'} usage={utTableUsage} universalTrackers={utrTable} />
      : null
    }

    <MaterialityModal
      isOpen={materialityModal}
      toggle={toggleMaterialityModal}
      initiative={initiative}
      handleReload={handleReload}
    />
  </>
}
